/**
 * Utility functions for converting and formatting meteorological units
 * between Imperial (°F, ft, in, mph) and Metric (°C, m, mm, cm, km/h).
 */

export type MeasurementUnits = 'imperial' | 'metric';

/**
 * Converts Fahrenheit to Celsius.
 */
export function fahrenheitToCelsius(f: number): number {
  return (f - 32) * (5 / 9);
}

/**
 * Converts Celsius to Fahrenheit.
 */
export function celsiusToFahrenheit(c: number): number {
  return (c * 9) / 5 + 32;
}

/**
 * Formats temperature given a value in Fahrenheit.
 */
export function formatTemp(
  f: number | null | undefined,
  units: MeasurementUnits = 'imperial',
  includeUnit = true,
  precision = 0
): string {
  if (f === null || f === undefined || isNaN(f)) return '--';
  const val = units === 'metric' ? fahrenheitToCelsius(f) : f;
  const rounded = precision > 0 ? val.toFixed(precision) : Math.round(val).toString();
  return includeUnit ? `${rounded}°${units === 'metric' ? 'C' : 'F'}` : rounded;
}

/**
 * Converts feet to meters.
 */
export function feetToMeters(ft: number): number {
  return ft * 0.3048;
}

/**
 * Converts meters to feet.
 */
export function metersToFeet(m: number): number {
  return m / 0.3048;
}

/**
 * Formats elevation or altitude given a value in feet.
 */
export function formatElevation(
  ft: number | null | undefined,
  units: MeasurementUnits = 'imperial',
  includeUnit = true,
  precision = 0
): string {
  if (ft === null || ft === undefined || isNaN(ft)) return '--';
  const val = units === 'metric' ? feetToMeters(ft) : ft;
  const rounded = precision > 0 ? val.toFixed(precision) : Math.round(val).toLocaleString();
  return includeUnit ? `${rounded} ${units === 'metric' ? 'm' : 'ft'}` : rounded;
}

/**
 * Converts miles per hour to km/h.
 */
export function mphToKmh(mph: number): number {
  return mph * 1.609344;
}

/**
 * Formats wind speed given a value in mph.
 */
export function formatWindSpeed(
  mph: number | null | undefined,
  units: MeasurementUnits = 'imperial',
  includeUnit = true,
  precision = 0
): string {
  if (mph === null || mph === undefined || isNaN(mph)) return '--';
  const val = units === 'metric' ? mphToKmh(mph) : mph;
  const rounded = precision > 0 ? val.toFixed(precision) : Math.round(val).toString();
  return includeUnit ? `${rounded} ${units === 'metric' ? 'km/h' : 'mph'}` : rounded;
}

/**
 * Converts inches of rain to millimeters.
 */
export function inchesToMm(inches: number): number {
  return inches * 25.4;
}

/**
 * Formats rain accumulation given a value in inches.
 */
export function formatRain(
  inches: number | null | undefined,
  units: MeasurementUnits = 'imperial',
  precision = 1
): string {
  if (inches === null || inches === undefined || isNaN(inches)) return '--';
  if (units === 'metric') {
    const mm = inchesToMm(inches);
    return `${mm.toFixed(precision)} mm`;
  }
  return `${inches.toFixed(precision)}"`;
}

/**
 * Converts inches of snow to centimeters.
 */
export function inchesToCm(inches: number): number {
  return inches * 2.54;
}

/**
 * Formats snow accumulation given a value in inches.
 */
export function formatSnow(
  inches: number | null | undefined,
  units: MeasurementUnits = 'imperial',
  precision = 1
): string {
  if (inches === null || inches === undefined || isNaN(inches)) return '--';
  if (units === 'metric') {
    const cm = inchesToCm(inches);
    return `${cm.toFixed(precision)} cm`;
  }
  return `${inches.toFixed(precision)}"`;
}

/**
 * Formats distance given a value in miles.
 */
export function formatDistance(
  miles: number | null | undefined,
  units: MeasurementUnits = 'imperial',
  precision = 1
): string {
  if (miles === null || miles === undefined || isNaN(miles)) return '--';
  if (units === 'metric') {
    const km = miles * 1.609344;
    return `${km.toFixed(precision)} km`;
  }
  return `${miles.toFixed(precision)} mi`;
}
